import type { ServicesApiEnvHealthResponse } from '@/services/api/services-api';

export const CATEGORY_LABELS: Record<string, string> = {
  identify: 'Profile updates',
  events: 'Incoming events',
  campaigns: 'Campaign processing',
  broadcasts: 'Broadcast sends',
  transactional: 'Transactional messages',
  delivery_metrics: 'Delivery metrics',
  csv_imports: 'CSV imports',
  integrations: 'Integrations',
  objects: 'Custom objects',
  segments: 'Segment updates',
};

export const EGRESS_CATEGORY_LABELS: Record<string, string> = {
  message_delivery: 'Message delivery',
  transactional_delivery: 'Transactional delivery',
  webhooks: 'Webhooks',
  journey_actions: 'Journey actions',
  alias: 'Profile merging',
  related_updates: 'Related data updates',
};

export const SERVICE_INFO_TOOLTIPS: Record<string, string> = {
  identify: 'Profile and identity data from your app, SDK, or API.',
  events: 'Custom events sent for segmentation and journey triggers.',
  campaigns: 'Campaign processing — starting, continuing, and retrying campaign workflows.',
  broadcasts: 'One-time messages to segments or lists.',
  transactional: 'API-triggered transactional messages (e.g. password reset, receipts).',
  delivery_metrics:
    'Delivery metrics processing — tracks sent, opened, clicked, bounced, and complaint events. This is not message delivery itself.',
  csv_imports: 'Profiles or events imported via CSV upload.',
  integrations: 'Data synced from connected integrations and Reverse ETL.',
  objects: 'Custom object data, relationships, and object-based segment conditions.',
  segments: 'Static segment changes and scheduled condition re-evaluation.',
  message_delivery: 'Campaign and broadcast delivery to email, push, and SMS.',
  transactional_delivery: 'Transactional message delivery to channels.',
  webhooks: 'Outbound webhook action calls from journeys.',
  journey_actions: 'Data lookup and profile update steps within journeys.',
  alias: 'Profile merge and anonymous event resolution.',
  related_updates: 'Cascading updates from object and relationship changes.',
};

export const CATEGORY_IMPACT: Record<string, string> = {
  identify: 'Profile updates may not appear immediately — segments and filters could show stale data.',
  events: 'Incoming events are queued — event-triggered journeys and workflows may fire late.',
  campaigns: 'Campaigns may take longer to progress to the next step.',
  broadcasts: 'Broadcast sends are backed up — delivery will be slower than expected.',
  transactional: 'Transactional messages may take longer to send.',
  delivery_metrics: 'Delivery metric processing is slow — sent, open, and click counts may be temporarily inaccurate.',
  csv_imports: 'CSV imports are still processing — imported profiles and attributes are not yet available.',
  integrations: 'Integration imports are still processing — imported profiles are not yet available.',
  objects:
    'Custom object updates are pending — relationships and object data may be outdated, and segments using object conditions may lag behind.',
  segments:
    'Segment membership updates are behind — static segment changes and scheduled re-evaluations may not reflect recent data.',
  message_delivery: 'Emails, push notifications, and SMS may arrive late.',
  transactional_delivery: 'Transactional messages (e.g. password resets) are waiting to be sent.',
  webhooks: 'Webhook requests from journeys may be delayed.',
  journey_actions: 'Journeys may be delayed at data lookup and profile update steps.',
  alias: 'Profile merging is delayed — some duplicate profiles may appear temporarily.',
  related_updates: 'Updates triggered by object and relationship changes may not reflect recent data.',
};

export function formatThroughput(perSecond: number): string {
  if (perSecond <= 0) return '0/s';
  if (perSecond >= 1) return `${Math.round(perSecond).toLocaleString()}/s`;
  const perMinute = perSecond * 60;
  if (perMinute >= 1) return `${Math.round(perMinute)}/min`;
  return '<1/min';
}

export function processingStatusLabel(rawDrainSeconds: number | null | undefined): {
  label: string;
  color: 'green' | 'yellow' | 'red';
} {
  const drainSeconds = rawDrainSeconds != null ? Math.round(rawDrainSeconds) : null;
  if (drainSeconds == null || drainSeconds < 180) {
    return { label: 'Current', color: 'green' };
  }
  if (drainSeconds < 600) {
    return { label: 'A few minutes behind', color: 'yellow' };
  }
  if (drainSeconds < 3600) {
    const mins = Math.round(drainSeconds / 60);
    return { label: `About ${mins} minutes behind`, color: 'red' };
  }
  const totalMins = Math.round(drainSeconds / 60);
  const hours = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  const duration = mins > 0 ? `${hours} hr ${mins} min` : `${hours} hr`;
  return { label: `About ${duration} behind`, color: 'red' };
}

export function formatCompactDuration(seconds: number): string {
  const rounded = Math.round(seconds);
  if (rounded < 60) return `${rounded}s`;
  const minutes = Math.round(rounded / 60);
  if (minutes < 60) return `${minutes}min`;
  const hours = Math.floor(minutes / 60);
  const remainingMins = minutes % 60;
  if (remainingMins === 0) return `${hours}h`;
  return `${hours}h ${remainingMins}min`;
}

export const HIGH_IMPACT_CATEGORIES = new Set([
  'identify',
  'events',
  'campaigns',
  'transactional',
  'segments',
  'message_delivery',
  'transactional_delivery',
  'journey_actions',
]);

export const SEVERITY_COLOR: Record<string, 'yellow' | 'red'> = {
  caution: 'yellow',
  error: 'red',
};

export const DELAY_STATUS_LABELS = {
  normal: 'Normal',
  slight_delay: 'Slight delay',
  delayed: 'Delayed',
  major_delay: 'Major delay',
};

export function delayStatus(
  delaySeconds: number | null | undefined,
  pending: number,
): { label: string; color: 'green' | 'yellow' | 'red' } {
  if (pending === 0 || delaySeconds == null || delaySeconds < 10) {
    return { label: DELAY_STATUS_LABELS.normal, color: 'green' };
  }
  if (delaySeconds < 60) return { label: DELAY_STATUS_LABELS.slight_delay, color: 'yellow' };
  if (delaySeconds < 300) return { label: DELAY_STATUS_LABELS.delayed, color: 'yellow' };
  return { label: DELAY_STATUS_LABELS.major_delay, color: 'red' };
}

const HEALTH_HEADER_STATUS_CONFIG: Record<
  ServicesApiEnvHealthResponse['health'],
  { heading: string; description: string }
> = {
  healthy: {
    heading: 'Healthy',
    description: 'Everything is running smoothly. No issues detected.',
  },
  busy: {
    heading: 'Busy',
    description: 'Your workspace is processing a higher volume than usual.',
  },
  degraded: {
    heading: 'Slow performance',
    description: 'Your workspace has pending issues that may be impacting performance.',
  },
};

function countSeverities(data: ServicesApiEnvHealthResponse): { high: number; medium: number; low: number } {
  const allCategories = [...data.processing.categories, ...data.egress.categories];
  let high = 0;
  let medium = 0;
  let low = 0;

  for (const cat of allCategories) {
    const status = delayStatus(cat.delay_seconds, cat.pending);
    if (status.color === 'red') high++;
    else if (status.color === 'yellow' && HIGH_IMPACT_CATEGORIES.has(cat.name)) medium++;
    else if (status.color === 'yellow') low++;
  }

  return { high, medium, low };
}

export function getHealthSummary(
  data: ServicesApiEnvHealthResponse | undefined,
  isIdentifiersMigrationInProgress: boolean,
) {
  if (data == null) {
    return {
      heading: HEALTH_HEADER_STATUS_CONFIG.healthy.heading,
      severities: { high: 0, medium: 0, low: 0 },
      hasIssues: false,
      info: [HEALTH_HEADER_STATUS_CONFIG.healthy.description],
    };
  }

  const { heading, description } = HEALTH_HEADER_STATUS_CONFIG[data.health];
  const severities = countSeverities(data);
  const hasIssues = severities.high > 0 || severities.medium > 0 || severities.low > 0;

  return {
    heading,
    severities,
    hasIssues,
    info: [
      isIdentifiersMigrationInProgress
        ? 'Identifiers migration in progress. All data processing related to people and events is stopped until the migration is complete. Updates to existing people are paused as well. Everything else will continue to work as usual.'
        : '',
      description,
    ].filter(Boolean),
  };
}
